import { useState } from "react";

/* ── shared sub-components ── */
function PCardBadge({ children, variant }) {
  const styles = {
    mn: "bg-[rgba(109,211,167,.08)] border-[rgba(109,211,167,.15)] text-mint",
    ai: "bg-[rgba(216,90,48,.1)] border-[rgba(216,90,48,.2)] text-[#D85A30]",
    cy: "bg-[rgba(26,144,112,.1)] border-[rgba(26,144,112,.22)] text-[#22B58A]",
    go: "bg-[rgba(242,185,73,.08)] border-[rgba(242,185,73,.18)] text-gold",
    pu: "bg-[rgba(155,109,211,.08)] border-[rgba(155,109,211,.18)] text-[#B48FE8]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[.48rem] tracking-[.1em] uppercase font-bold px-2 py-[3px] rounded-full border w-fit ${styles[variant]}`}
    >
      {children}
    </span>
  );
}

const cardAccent = {
  mn: "#6DD3A7",
  ai: "#D85A30",
  cy: "#1A9070",
  go: "#F2B949",
  pu: "#9B6DD3",
};

const ctaStyles = {
  mn: {
    idle: "text-mint bg-[rgba(109,211,167,.07)] border-[rgba(109,211,167,.16)]",
    hover: "hover:bg-mint hover:text-ink hover:border-mint",
  },
  ai: {
    idle: "text-[#D85A30] bg-[rgba(216,90,48,.06)] border-[rgba(216,90,48,.14)]",
    hover: "hover:bg-[#D85A30] hover:text-white hover:border-[#D85A30]",
  },
  cy: {
    idle: "text-[#22B58A] bg-[rgba(26,144,112,.06)] border-[rgba(26,144,112,.14)]",
    hover: "hover:bg-[#1A9070] hover:text-white hover:border-[#1A9070]",
  },
  go: {
    idle: "text-gold bg-[rgba(242,185,73,.06)] border-[rgba(242,185,73,.14)]",
    hover: "hover:bg-gold hover:text-ink hover:border-gold",
  },
  pu: {
    idle: "text-[#B48FE8] bg-[rgba(155,109,211,.06)] border-[rgba(155,109,211,.14)]",
    hover: "hover:bg-[#9B6DD3] hover:text-white hover:border-[#9B6DD3]",
  },
};

function ProductCard({
  href,
  variant = "mn",
  badge,
  num,
  name,
  desc,
  dur,
  price,
  originalPrice,
  saving,
  priceContact,
  step,
  delay,
  isBundleStar,
}) {
  const cta = ctaStyles[variant];
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      className="group relative flex flex-col bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] overflow-hidden no-underline text-inherit transition-all duration-300 hover:-translate-y-[3px] hover:border-[rgba(109,211,167,.26)] hover:shadow-[0_14px_36px_rgba(8,24,40,.5)] anim-up"
      style={{ animationDelay: `${delay}s` }}
    >
      {/* top accent bar */}
      <span
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: cardAccent[variant] }}
      />

      <div className="p-[clamp(14px,1.6vw,20px)] flex flex-col flex-1">
        {/*
          header row — all badges in one flex line, nothing absolute:
          [step?] [variant badge]  →  spacer  ←  [saving? | popular?]
        */}
        <div className="flex items-center gap-[6px] mb-[.75rem]">
          {/* step circle */}
          {step && (
            <span
              className="w-[20px] h-[20px] rounded-full flex-shrink-0 flex items-center justify-center font-mono text-[.46rem] font-bold text-ink"
              style={{ background: cardAccent[variant] }}
            >
              {step}
            </span>
          )}

          {/* variant badge */}
          <PCardBadge variant={variant}>{badge}</PCardBadge>

          {/* push right */}
          <span className="flex-1" />

          {/* saving badge */}
          {saving && (
            <span className="bg-[#D85A30] text-white text-[.46rem] font-bold font-cairo px-[6px] py-[2px] rounded-[5px] leading-none flex-shrink-0">
              وفر {saving} ر
            </span>
          )}

          {/* most-popular badge */}
          {isBundleStar && (
            <span className="bg-[#F2B949] text-ink text-[.46rem] font-bold font-cairo px-[6px] py-[2px] rounded-[5px] leading-none flex-shrink-0">
              الأكثر طلباً ★
            </span>
          )}
        </div>
        {num && (
          <span className="font-mono text-[.48rem] text-t-dim tracking-[.06em] block mb-[.3rem]">
            {num}
          </span>
        )}
        <p
          className="font-cairo font-extrabold text-t-primary leading-[1.3] tracking-tight mb-[.4rem] flex-1"
          style={{ fontSize: "clamp(.86rem,1.05vw,.98rem)" }}
        >
          {name}
        </p>
        <p className="text-[.7rem] text-t-sec leading-[1.6] mb-[.8rem]">
          {desc}
        </p>

        <div className="flex items-center justify-between mb-[.75rem]">
          <span className="font-mono text-[.54rem] text-t-dim tracking-[.05em]">
            {dur}
          </span>
          {priceContact ? (
            <span className="font-cairo text-[.78rem] font-bold text-t-sec">
              تواصل معنا
            </span>
          ) : (
            <div className="flex flex-col items-end gap-[2px]">
              {originalPrice && (
                <span className="font-cairo text-[.65rem] text-t-dim line-through">
                  {originalPrice} ر
                </span>
              )}
              <span className="font-cairo text-[.92rem] font-bold text-gold">
                {price} ر
              </span>
            </div>
          )}
        </div>

        <div
          className={`flex items-center justify-center gap-1.5 px-3.5 py-[9px] rounded-[4px] border text-[.8rem] font-cairo font-bold transition-all duration-200 ${cta.idle} ${cta.hover}`}
        >
          <svg
            className="w-3 h-3 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
          احجز في سلة
        </div>
      </div>
    </a>
  );
}

function TrackHeader({
  icon,
  title,
  sub,
  price,
  originalPrice,
  note,
  variant,
}) {
  const col = variant === "ai" ? "#D85A30" : "#1A9070";
  return (
    <div className="relative flex items-center gap-3 bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] overflow-hidden mb-[clamp(14px,2vh,20px)] p-[clamp(12px,1.5vw,18px)_clamp(14px,2vw,22px)]">
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: col }}
      />
      <span className="text-[1.3rem] flex-shrink-0">{icon}</span>
      <div className="flex-1">
        <p
          className="font-cairo font-extrabold text-t-primary mb-[.2rem]"
          style={{ fontSize: "clamp(.88rem,1.1vw,1rem)" }}
        >
          {title}
        </p>
        <p className="text-[.72rem] text-t-sec">{sub}</p>
      </div>
      <div className="mr-auto text-left flex-shrink-0">
        {originalPrice && (
          <span className="font-cairo text-[.65rem] text-t-dim line-through block">
            {originalPrice} ر
          </span>
        )}
        <span className="font-cairo text-[.78rem] font-bold text-gold block">
          {price}
        </span>
        <span className="font-mono text-[.58rem] text-t-dim">{note}</span>
      </div>
    </div>
  );
}

/* ── Tab config ── */
const TAB_CONFIG = [
  {
    id: "data",
    label: "مسارات البيانات",
    activeClass: "bg-mint text-ink border-mint",
  },
  {
    id: "cyber",
    label: "الأمن السيبراني",
    activeClass: "bg-[#1A9070] text-white border-[#1A9070]",
  },
  {
    id: "ai",
    label: "الذكاء الاصطناعي",
    activeClass: "bg-[#D85A30] text-white border-[#D85A30]",
  },
  {
    id: "workshops",
    label: "ورش عمل",
    activeClass: "bg-mint text-ink border-mint",
  },
  {
    id: "strengths",
    label: "مواطن القوة",
    activeClass: "bg-gold text-ink border-gold",
  },
];

/* ══════════════════════════════════════════════
   PANEL DATA — updated from Salla screenshots
   ══════════════════════════════════════════════ */

const DATA_CARDS = [
  {
    href: "https://store.afaaqwetaan.com/%D9%85%D8%B3%D8%A7%D8%B1-%D8%AA%D8%AD%D9%84%D9%8A%D9%84-%D8%A7%D9%84%D8%A8%D9%8A%D8%A7%D9%86%D8%A7%D8%AA/p1610920966",
    variant: "mn",
    badge: "DATA · D-01",
    num: "ID 1610920966",
    name: "مسار تحليل البيانات",
    desc: "Python · SQL · Excel · Power BI + DAX · Azure ETL — مسار متكامل من الصفر حتى الاحتراف مع مشروع تخرج.",
    dur: "120 HRS · ONLINE",
    price: "3000",
  },
  {
    href: "https://store.afaaqwetaan.com/data-analysis-track-ml/p431085344",
    variant: "mn",
    badge: "DATA + ML · D-02",
    num: "ID 431085344",
    name: "Data Analysis Track + ML",
    desc: "مسار تحليل البيانات الكامل مضافاً إليه تعلم الآلة وبناء النماذج — الباقة الأشمل للمهتمين بالبيانات والذكاء الاصطناعي.",
    dur: "INTENSIVE",
    price: "2000",
  },
  {
    href: "https://store.afaaqwetaan.com/%D9%85%D8%B9%D8%B3%D9%83%D8%B1-%D8%B9%D9%84%D9%85-%D8%A7%D9%84%D8%A8%D9%8A%D8%A7%D9%86%D8%A7%D8%AA-%D8%A7%D9%84%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D9%8A-%D9%88%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/p1837855112",
    variant: "ai",
    badge: "DATA + AI · D-03",
    num: "ID 1837855112",
    name: "معسكر علم البيانات التطبيقي والذكاء الاصطناعي",
    desc: "من تحليل البيانات والبرمجة إلى Generative AI · Deep Learning · Machine Learning — يُركّز على التطبيق ومشاريع السوق.",
    dur: "INTENSIVE",
    price: "3000",
  },
];

/* ── CYBER: ترتيب حسب الصور — C-03 أولاً ثم C-02 ثم C-01 ثم C-04 ثم C-05 ── */
const CYBER_CARDS = [
  {
    // البوابة الثالثة — أساسيات أمن الشبكات (تظهر أولاً في الصور)
    href: "https://store.afaaqwetaan.com/%D8%A3%D8%B3%D8%A7%D8%B3%D9%8A%D8%A7%D8%AA-%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B4%D8%A8%D9%83%D8%A7%D8%AA-%D9%88%D8%AA%D8%AD%D9%84%D9%8A%D9%84-%D8%A7%D9%84%D8%AD%D8%B2%D9%85/p2027148318",
    step: "G1",
    badge: "CYBER · C-01",
    name: "أساسيات الأمن السيبراني والهندسة الاجتماعية",
    desc: "كيف يفكر المهاجم · أنواع الهجمات · Social Engineering · Phishing · Kali Linux",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    // البوابة الثانية — أساسيات أمن تطبيقات الويب
    href: "https://store.afaaqwetaan.com/%D8%A3%D8%B3%D8%A7%D8%B3%D9%8A%D8%A7%D8%AA-%D8%A3%D9%85%D9%86-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D8%A7%D8%AA-%D8%A7%D9%84%D9%88%D9%8A%D8%A8/p543134201",
    step: "G2",
    badge: "CYBER · C-02",
    name: "أساسيات أمن تطبيقات الويب",
    desc: "OWASP Top 10 · Burp Suite · SQL Injection · XSS · DVWA · WAF · تقرير ثغرات احترافي",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/%D8%A3%D8%B3%D8%A7%D8%B3%D9%8A%D8%A7%D8%AA-%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A-%D9%88%D8%A7%D9%84%D9%87%D9%86%D8%AF%D8%B3%D8%A9-%D8%A7%D9%84%D8%A7%D8%AC%D8%AA%D9%85%D8%A7%D8%B9%D9%8A%D8%A9/p1788324593",
    step: "G3",
    badge: "CYBER · C-03",
    name: "أساسيات أمن الشبكات وتحليل الحزم",
    desc: "Nmap · Wireshark · IDS/IPS · جدران الحماية · تشفير الاتصالات · خطة حماية الشبكة",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/devsecops-%D9%88%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%A3%D9%83%D9%88%D8%A7%D8%AF-%D9%88%D8%A7%D9%84%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D8%A7%D8%AA/p1117768535",
    step: "G4",
    badge: "CYBER · C-04",
    name: "DevSecOps وأمن الأكواد والتطبيقات",
    desc: "Shift-Left Security · SAST · GitHub Actions · Secrets Management · Docker الآمن · Cloud Pipeline",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/%D8%A7%D8%AE%D8%AA%D8%A8%D8%A7%D8%B1-%D8%A7%D9%84%D8%A7%D8%AE%D8%AA%D8%B1%D8%A7%D9%82-%D9%88%D8%A7%D9%84%D8%AA%D9%82%D8%A7%D8%B1%D9%8A%D8%B1-%D8%A7%D9%84%D8%A3%D9%85%D9%86%D9%8A%D8%A9/p1576467493",
    step: "G5",
    badge: "CYBER · C-05",
    name: "اختبار الاختراق والتقارير الأمنية",
    desc: "Pentest منهجي شامل · CVSS · Executive Summary · Technical Report · خطة الاستجابة للحوادث",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    // الباقة الكاملة Cyber
    href: "https://store.afaaqwetaan.com/%D9%85%D8%B3%D8%A7%D8%B1-%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A-%D9%81%D9%8A-%D8%A7%D9%84%D8%A8%D8%B1%D9%85%D8%AC%D9%8A%D8%A7%D8%AA/p1",
    badge: "BUNDLE · CYBER FULL",
    name: "مسار الأمن السيبراني في البرمجيات — الباقة الكاملة",
    desc: "٥ بوابات كاملة · أساسيات الأمن والهندسة الاجتماعية · أمن الويب · أمن الشبكات · DevSecOps · اختبار الاختراق",
    dur: "10 WKS · 300+ HRS",
    price: "4625",
    originalPrice: "6750",
    saving: "2125",
    isBundleStar: true,
  },
];

/* ── AI: ترتيب البوابات حسب الصور ── */
const AI_CARDS = [
  {
    href: "https://store.afaaqwetaan.com/%D8%AA%D8%AD%D9%84%D9%8A%D9%84-%D8%A7%D9%84%D8%A8%D9%8A%D8%A7%D9%86%D8%A7%D8%AA-%D8%A8%D9%88%D8%A7%D8%B3%D8%B7%D8%A9-ai-%D9%84%D9%84%D9%88%D8%B5%D9%88%D9%84-%D8%A5%D9%84%D9%89-%D9%82%D8%B1%D8%A7%D8%B1%D8%A7%D8%AA/p845652276",
    step: "G1",
    badge: "AI · A-01",
    name: "تحليل البيانات بواسطة AI للوصول إلى قرارات ذكية",
    desc: "Pandas · Matplotlib · EDA · تنظيف البيانات · لوحة تحليلية تفاعلية · تهيئة للنمذجة",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/%D8%AA%D8%B9%D9%84%D9%85-%D8%A7%D9%84%D8%A2%D9%84%D8%A9-%D9%88%D8%A8%D9%86%D8%A7%D8%A1-%D8%A7%D9%84%D9%86%D9%85%D8%A7%D8%B0%D8%AC-%D9%88%D8%A7%D9%84%D8%AA%D8%B9%D8%A7%D9%85%D9%84-%D9%85%D8%B9%D9%87%D8%A7/p1075683787",
    step: "G2",
    badge: "AI · A-02",
    name: "تعلم الآلة وبناء النماذج والتعامل معها",
    desc: "Supervised Learning · Random Forest · XGBoost · Optuna · تفسير النماذج لأصحاب القرار",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/%D9%85%D8%B9%D8%A7%D9%84%D8%AC%D8%A9-%D8%A7%D9%84%D9%84%D8%BA%D8%A9-%D8%A7%D9%84%D8%B7%D8%A8%D9%8A%D8%B9%D9%8A%D8%A9-%D9%88%D8%A8%D9%86%D8%A7%D8%A1-%D9%86%D9%85%D8%A7%D8%B0%D8%AC-nlp/p1766138575",
    step: "G3",
    badge: "AI · A-03",
    name: "معالجة اللغة الطبيعية وبناء نماذج NLP",
    desc: "BERT · AraBERT · Fine-Tuning · تحليل المشاعر العربي · نشر النموذج كـ API · Gradio",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/%D8%B1%D8%A8%D8%B7-%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A-%D8%A8%D8%A7%D9%84%D8%A3%D8%B9%D9%85%D8%A7%D9%84/p959116254",
    step: "G4",
    badge: "AI · A-04",
    name: "ربط الذكاء الاصطناعي بالأعمال — Power BI وربطها API",
    desc: "Power BI · Tableau · Streamlit · ربط النماذج بـ KPIs تجارية · عرض النتائج بلغة المدير",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    href: "https://store.afaaqwetaan.com/%D8%A8%D9%86%D8%A7%D8%A1-%D9%88%D8%AA%D8%B7%D9%88%D9%8A%D8%B1-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D8%A7%D8%AA-%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/p634919463",
    step: "G5",
    badge: "AI · A-05",
    name: "بناء وتطوير تطبيقات الذكاء الاصطناعي",
    desc: "تصميم بنية الحل · بناء النموذج · GitHub Actions · واجهة المستخدم · مشروع تخرج شامل",
    dur: "2 WKS · 60 HRS",
    price: "1350",
    originalPrice: "1750",
    saving: "400",
  },
  {
    // الباقة الكاملة AI
    href: "https://store.afaaqwetaan.com/%D9%85%D8%B3%D8%A7%D8%B1-%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A-%D9%81%D9%8A-%D8%A7%D9%84%D8%A3%D8%B9%D9%85%D8%A7%D9%84/p2",
    badge: "BUNDLE · AI FULL",
    name: "مسار الذكاء الاصطناعي في الأعمال — الباقة الكاملة",
    desc: "٥ بوابات كاملة · تحليل البيانات · تعلم الآلة · NLP · ربط بالأعمال · بناء تطبيقات AI",
    dur: "10 WKS · 300+ HRS",
    price: "4625",
    originalPrice: "6750",
    saving: "2125",
    isBundleStar: true,
  },
];

export default function Store() {
  const [activeTab, setActiveTab] = useState("data");

  return (
    <section
      className="relative overflow-hidden px-[clamp(16px,5vw,80px)] py-[clamp(64px,9vh,110px)]"
      id="store"
    >
      {/* top divider */}
      <div
        className="absolute top-0 left-[clamp(16px,5vw,80px)] right-[clamp(16px,5vw,80px)] h-px"
        style={{
          background:
            "linear-gradient(90deg,transparent,rgba(109,211,167,0.11) 30%,rgba(109,211,167,0.11) 70%,transparent)",
        }}
      />
      {/* bg glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 40% at 50% 0%,rgba(109,211,167,0.04) 0%,transparent 70%)",
        }}
      />

      {/* ── Header ── */}
      <div
        className="text-center mb-[clamp(36px,5vh,56px)] relative z-[2] anim-up"
        style={{ animationDelay: ".05s" }}
      >
        <span className="inline-flex items-center gap-2 font-mono text-[.68rem] tracking-[.2em] uppercase text-mint font-semibold mb-[1.2rem] before:content-['◆'] before:text-[.4rem] before:opacity-40 after:content-['◆'] after:text-[.4rem] after:opacity-40">
          AFQ STORE · متجر التذاكر
        </span>
        <h2
          className="font-cairo font-black leading-[1.1] tracking-tight text-t-primary mb-[.6rem]"
          style={{ fontSize: "clamp(1.9rem,3.5vw,3.1rem)" }}
        >
          احجز <em className="not-italic text-mint">تذكرتك</em>
        </h2>
        <p className="text-[.93rem] text-t-sec leading-[1.85] max-w-[480px] mx-auto">
          كل المنتجات المتاحة في متجر آفاق — اختر ما يناسبك وانطلق مباشرة.
        </p>
      </div>

      {/* ── Tabs ── */}
      <div
        className="flex items-center justify-center gap-1.5 flex-wrap mb-[clamp(28px,4vh,44px)] relative z-[2] anim-up"
        style={{ animationDelay: ".15s" }}
        role="tablist"
      >
        {TAB_CONFIG.map(({ id, label, activeClass }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            role="tab"
            aria-selected={activeTab === id}
            className={[
              "font-mono text-[.6rem] tracking-[.1em] uppercase font-semibold px-4 py-2 rounded-full border transition-all duration-200",
              activeTab === id
                ? activeClass
                : "border-[rgba(109,211,167,0.11)] text-t-dim hover:border-[rgba(109,211,167,.25)] hover:text-t-sec",
            ].join(" ")}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Panels ── */}
      <div className="relative z-[2]">
        {/* DATA */}
        {activeTab === "data" && (
          <div
            className="grid gap-[clamp(10px,1.2vw,14px)]"
            style={{ gridTemplateColumns: "repeat(3,1fr)" }}
          >
            {DATA_CARDS.map((c, i) => (
              <ProductCard key={c.badge} {...c} delay={0.05 + i * 0.05} />
            ))}
          </div>
        )}

        {/* CYBER */}
        {activeTab === "cyber" && (
          <>
            <TrackHeader
              icon="🔐"
              variant="cy"
              title="مسار الأمن السيبراني في البرمجيات"
              sub="٥ بوابات متسلسلة · كل بوابة أسبوعان مكثفان · ٣٠٠+ ساعة · أونلاين مع تسجيل"
              price="4625 ر — الباقة الكاملة"
              originalPrice="6750"
              note="1350 ر / بوابة منفردة"
            />
            <div
              className="grid gap-[clamp(10px,1.2vw,14px)]"
              style={{ gridTemplateColumns: "repeat(3,1fr)" }}
            >
              {CYBER_CARDS.map((c, i) => (
                <ProductCard
                  key={c.badge}
                  {...c}
                  variant="cy"
                  delay={0.05 + i * 0.05}
                />
              ))}
            </div>
          </>
        )}

        {/* AI */}
        {activeTab === "ai" && (
          <>
            <TrackHeader
              icon="🤖"
              variant="ai"
              title="مسار الذكاء الاصطناعي في الأعمال"
              sub="٥ بوابات متسلسلة · كل بوابة أسبوعان مكثفان · ٣٠٠+ ساعة · أونلاين مع تسجيل"
              price="4625 ر — الباقة الكاملة"
              originalPrice="6750"
              note="1350 ر / بوابة منفردة"
            />
            <div
              className="grid gap-[clamp(10px,1.2vw,14px)]"
              style={{ gridTemplateColumns: "repeat(3,1fr)" }}
            >
              {AI_CARDS.map((c, i) => (
                <ProductCard
                  key={c.badge}
                  {...c}
                  variant="ai"
                  delay={0.05 + i * 0.05}
                />
              ))}
            </div>
          </>
        )}

        {/* WORKSHOPS */}
        {activeTab === "workshops" && (
          <div
            className="grid gap-[clamp(10px,1.2vw,14px)] max-w-[700px] mx-auto"
            style={{ gridTemplateColumns: "repeat(2,1fr)" }}
          >
            <ProductCard
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D9%81%D9%8A-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A-%D9%81%D9%8A-%D9%88%D8%B8%D9%8A%D9%81%D8%AA%D9%83/p829257959"
              variant="mn"
              badge="WORKSHOP · W-01"
              name="الذكاء الاصطناعي في وظيفتك"
              desc="خطة تطوير مهني مبنية على الذكاء الاصطناعي — تحضير المقابلات، بناء البورتفوليو، تحديد المسار، والوصول للفرص. + ملف Prompts جاهز."
              dur="DIGITAL · SELF-PACED"
              price="189"
              delay={0.05}
            />
            <ProductCard
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%AC%D8%AA%D9%85%D8%A7%D8%B9%D9%8A-%D9%84%D8%AA%D9%86%D9%85%D9%88-%D9%81%D9%8A-%D9%88%D8%B8%D9%8A%D9%81%D8%AA%D9%83-%D9%82%D8%B1%D9%8A%D8%A8%D8%A7/p2072180820"
              variant="mn"
              badge="WORKSHOP · W-02"
              name="الذكاء الاجتماعي لتنمو في وظيفتك"
              desc={
                <>
                  إدارة الانطباع، التواصل الذكي، التعامل مع الشخصيات الصعبة،
                  وعرض إنجازاتك. + عبارات مهنية جاهزة.{" "}
                  <span className="text-gold text-[.65rem]">(قريباً)</span>
                </>
              }
              dur="DIGITAL · SELF-PACED"
              price="189"
              delay={0.1}
            />
          </div>
        )}

        {/* STRENGTHS */}
        {activeTab === "strengths" && (
          <div
            className="grid gap-[clamp(10px,1.2vw,14px)]"
            style={{ gridTemplateColumns: "repeat(3,1fr)" }}
          >
            <ProductCard
              href="https://store.afaaqwetaan.com/%D8%A8%D8%A7%D9%82%D8%A9-%D9%85%D9%88%D8%A7%D8%B7%D9%86-%D8%A7%D9%84%D9%82%D9%88%D8%A9/p539214370"
              variant="go"
              badge="STRENGTHS · S-01"
              name="باقة مواطن القوة — Gallup CliftonStrengths"
              desc="تقرير CliftonStrengths المفصّل + ورشة سمات تفاعلية مع كوتش منذر الحنبلي وكوتش لطيفة القريشي + تطبيقات عملية. مقاعد محدودة: 7–10."
              dur="3 DAYS · ONLINE"
              price="750"
              delay={0.05}
            />
            <ProductCard
              href="https://store.afaaqwetaan.com/%D8%AC%D9%84%D8%B3%D8%A9-%D9%83%D9%88%D8%AA%D8%B4%D9%86%D9%82-%D9%81%D8%B1%D8%AF%D9%8A%D8%A9/p418885937"
              variant="go"
              badge="COACHING · S-02"
              name="جلسة كوتشنق فردية"
              desc="جلسة فردية ٤٥ دقيقة مع كوتش لطيفة القريشي لتحليل تقرير مواطن قوتك واتخاذ قرارات مهنية أوضح. أو باقة ٣ جلسات مكثفة لبناء خطة تطوير شخصية."
              dur="45 MIN · ONLINE"
              priceContact
              delay={0.1}
            />
            <ProductCard
              href="https://store.afaaqwetaan.com/%D8%A3%D8%AC%D9%86%D8%AF%D8%A9-%D8%A2%D9%81%D8%A7%D9%82-2026/p2009678366"
              variant="pu"
              badge="AGENDA · S-03"
              name="أجندة آفاق | ٢٠٢٦"
              desc="مساحة واعية للتفكير والتعلم واكتشاف ما يميّزك. تخطيط شهري، عبارات تطويرية، وصفحات تأمل تحفّزك على النمو طوال العام."
              dur="DIGITAL · PDF"
              priceContact
              delay={0.15}
            />
          </div>
        )}
      </div>
    </section>
  );
}
